import { useEffect, useState } from "react";
import { AlertTriangleIcon, InfoIcon, RefreshCwIcon } from "lucide-react";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { num } from "@/lib/matching";
import { formatDate, formatDateTime, formatNumber } from "@/lib/format";

type InboxRoutingCompanyRow = components["schemas"]["InboxRoutingCompanyRow"];
type CompanyRoutingOutcome = NonNullable<components["schemas"]["CompanyRoutingOutcome"]>;

/** Everything - dates included - comes from the page filter, the same as every other section. */
function buildQuery(apiParams: URLSearchParams) {
  return {
    From: apiParams.get("From") ?? "",
    To: apiParams.get("To") ?? "",
    ClientIds: apiParams.getAll("ClientIds"),
    OrganisationId: apiParams.get("OrganisationId") || undefined,
  };
}

/** Whole percentages, except below 1% where rounding would print "0%" for a real share. */
function pct(fraction: number): string {
  const value = fraction * 100;
  if (value > 0 && value < 1) return `${value.toFixed(1)}%`;
  return `${Math.round(value)}%`;
}

/** Nullable 0-1 fraction (or its string). Null means "can't show this" and is never rendered as 0%. */
function toFraction(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}

// Keyed by CompanyRoutingOutcome (InboxRoutingAdminEndpoint.cs); fixed order and always shown, so zeros are visible.
const OUTCOME_LABELS: Record<CompanyRoutingOutcome, string> = {
  AutoAssigned: "Auto-assigned",
  BelowThreshold: "Below confidence threshold",
  Ambiguous: "Ambiguous candidates",
  HeadOffice: "Head office bill",
  MultipleDeliveryAddresses: "Multiple delivery addresses",
  NoDeliveryAddress: "No delivery address",
  NoOrganisations: "No organisations to match",
  AutoAssignDisabled: "Auto-assign disabled",
};
const OUTCOME_ORDER = Object.keys(OUTCOME_LABELS) as CompanyRoutingOutcome[];

function CompanyEmptyState({ companyName, clientName }: { companyName: string; clientName: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{companyName}</CardTitle>
        <CardDescription>{clientName}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground py-4 text-center text-sm">
          No bills arrived in this company inbox in this date range.
        </p>
      </CardContent>
    </Card>
  );
}

function CountTile({ label, count, hint }: { label: string; count: number; hint?: string }) {
  return (
    <div className="bg-muted/40 rounded-lg border p-4">
      <div className="flex items-center gap-1.5">
        <p className="text-muted-foreground text-xs">{label}</p>
        {hint && <InfoHint label={label}>{hint}</InfoHint>}
      </div>
      <p className="mt-1 text-3xl font-semibold tabular-nums">{formatNumber(count)}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function CompanyPanel({ row }: { row: InboxRoutingCompanyRow }) {
  const routed = row.routed;
  const unrouted = row.unrouted;
  const lag = row.lag;

  const autoCount = num(routed.auto);
  const manualCount = num(routed.manual);
  const assignedCount = num(routed.total);
  const unassignedCount = num(unrouted.total);
  const arrivedCount = assignedCount + unassignedCount;

  const timingAutoCount = num(lag.withinThresholdCount);
  const timingManualCount = num(lag.overThresholdCount);
  const measuredCount = num(lag.coveredRows);
  const assignedRowsForTiming = num(lag.totalRoutedRows);
  const thresholdSeconds = num(lag.thresholdSeconds);
  const disagreementCount = num(lag.disagreementCount);
  // Bills the timing check could not put a verdict on at all (no extraction timestamp, or a negative gap
  // excluded as re-extracted after assignment). Floored at 0 defensively.
  const noTimingDataCount = Math.max(assignedRowsForTiming - measuredCount, 0);

  if (arrivedCount === 0) {
    return <CompanyEmptyState companyName={row.companyName} clientName={row.clientName} />;
  }

  const nonAutoShare = toFraction(row.nonAutoShareOfArrivals);
  const nonAutoCount = manualCount + unassignedCount;
  const manualOfAssigned = toFraction(row.manualShareOfAssigned);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">{row.companyName}</CardTitle>
        <CardDescription>{row.clientName}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {nonAutoShare === null ? (
          <div className="rounded-lg border border-dashed p-4">
            <p className="text-base font-medium">This percentage is not available for a single organisation.</p>
            <p className="text-muted-foreground mt-1 text-sm">
              Bills still waiting in the inbox have not been given an organisation yet, so they cannot be
              counted under one. Clear the organisation filter above to see the number for the whole company
              inbox.
            </p>
          </div>
        ) : (
          <div>
            <p className="text-xl leading-snug font-medium">
              <span className="text-4xl font-semibold tabular-nums">{pct(nonAutoShare)}</span> of bills that
              landed in the company inbox were not assigned automatically.
            </p>
            <p className="text-muted-foreground mt-2 text-sm">
              That is {formatNumber(nonAutoCount)} of {formatNumber(arrivedCount)} inbox bills: assigned by a
              person, or still waiting.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <CountTile
            label="Arrived"
            count={arrivedCount}
            hint="Every bill that landed in this company inbox in the selected date range. Bills uploaded straight to an organisation are not counted here."
          />
          <CountTile label="Assigned automatically" count={autoCount} />
          <CountTile label="Assigned manually" count={manualCount} />
          <CountTile
            label="Still unassigned"
            count={unassignedCount}
            hint="Sitting in the company inbox with no organisation yet."
          />
        </div>

        <p className="text-sm">
          {formatNumber(autoCount)} of {formatNumber(arrivedCount)} were assigned automatically.
        </p>

        {/* 3. The audit check only speaks up when it disagrees with the recorded method. */}
        {disagreementCount > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
            <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
            <span>
              A separate timing check disagrees with the recorded method on {formatNumber(disagreementCount)}{" "}
              {disagreementCount === 1 ? "bill" : "bills"}. Open Data details below to see it.
            </span>
          </div>
        )}

        <Accordion type="single" collapsible className="border-t">
          <AccordionItem value="details" className="border-b-0">
            <AccordionTrigger>Data details</AccordionTrigger>
            <AccordionContent className="space-y-5 text-sm">
              <div>
                <p className="font-medium">How the percentage is worked out</p>
                <p className="text-muted-foreground mt-1">
                  Assigned manually plus still unassigned, over everything that arrived in the company inbox.
                </p>
                <p className="mt-1 tabular-nums">
                  ({formatNumber(manualCount)} + {formatNumber(unassignedCount)}) of{" "}
                  {formatNumber(arrivedCount)} = {nonAutoShare === null ? "not available" : pct(nonAutoShare)}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-medium">Assigned manually, of bills assigned to an org</p>
                  <InfoHint label="Assigned manually, of bills assigned to an org">
                    A second way to measure the same thing, leaving out bills that are still waiting. It is
                    always the friendlier number, because it ignores the backlog.
                  </InfoHint>
                </div>
                <p className="mt-1 tabular-nums">
                  {manualOfAssigned === null ? "Not available" : pct(manualOfAssigned)} (
                  {formatNumber(manualCount)} of {formatNumber(assignedCount)})
                </p>
              </div>

              <div>
                <p className="font-medium">How the manual assignments were made</p>
                <div className="mt-1 divide-y">
                  <DetailRow
                    label="Matched the suggestion"
                    value={formatNumber(routed.manualMatchingSuggestion)}
                  />
                  <DetailRow label="Chose a different org" value={formatNumber(routed.manualAgainstSuggestion)} />
                  <DetailRow label="No suggestion offered" value={formatNumber(routed.manualNoSuggestion)} />
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  A bill where the person picked the org that was already suggested is a confidence setting to
                  tune, not a matching failure.
                </p>
              </div>

              <div>
                <p className="font-medium">Why bills are still unassigned</p>
                <div className="mt-1 divide-y">
                  {OUTCOME_ORDER.map((key) => (
                    <DetailRow
                      key={key}
                      label={OUTCOME_LABELS[key]}
                      value={formatNumber(unrouted.byOutcome[key] ?? 0)}
                    />
                  ))}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Oldest waiting since {formatDateTime(unrouted.oldestWaitingSince)}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-medium">Timing check</p>
                  <InfoHint label="Timing check">
                    The recorded assignment method is the source of truth for every number above. This is an
                    independent check on it: a bill assigned within {thresholdSeconds} seconds of being read
                    was almost certainly assigned by the system, and one assigned later almost certainly by a
                    person. It is here to catch a recorded method that looks wrong, not to compete with it.
                  </InfoHint>
                </div>
                <div className="mt-1 divide-y">
                  <DetailRow
                    label={`Looks automatic (within ${thresholdSeconds}s)`}
                    value={formatNumber(timingAutoCount)}
                  />
                  <DetailRow
                    label={`Looks manual (over ${thresholdSeconds}s)`}
                    value={formatNumber(timingManualCount)}
                  />
                  <DetailRow label="Disagrees with the recorded method" value={formatNumber(disagreementCount)} />
                  <DetailRow label="No timing data" value={formatNumber(noTimingDataCount)} />
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Timing check available for {formatNumber(measuredCount)} of{" "}
                  {formatNumber(assignedRowsForTiming)} assigned bills.
                </p>
              </div>

              <p className="text-muted-foreground text-xs">
                Bills uploaded straight to an organisation never went through inbox assignment and are left out
                of every number in this section.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}

export interface InboxRoutingPanelProps {
  apiParams: URLSearchParams;
}

/** Section 5: how company-inbox bills get an organisation, auto vs by hand. The headline counts bills still
 * parked in the inbox as not automatic; the backlog-excluded share is in Data details. */
export function InboxRoutingPanel({ apiParams }: InboxRoutingPanelProps) {
  const apiParamsKey = apiParams.toString();
  const [rows, setRows] = useState<InboxRoutingCompanyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // The dates in force, printed so a screenshot says what period it covers.
  const windowLabel = `${formatDate(apiParams.get("From") ?? "")} to ${formatDate(apiParams.get("To") ?? "")}`;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/analytics/inbox-routing", {
        params: { query: buildQuery(apiParams) },
      });
      if (cancelled) return;
      if (apiError || !data) {
        setError("Inbox routing figures could not be loaded.");
      } else {
        setRows(data);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParamsKey, refreshKey]);

  if (loading && rows.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <div className="bg-muted h-6 w-64 animate-pulse rounded" />
        <div className="bg-muted h-64 animate-pulse rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-destructive/30 bg-destructive/5 flex flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
        <AlertTriangleIcon className="text-destructive size-6" />
        <p className="text-sm font-medium">Could not load inbox routing</p>
        <p className="text-muted-foreground max-w-md text-sm">{error}</p>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="border-border hover:bg-accent mt-1 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition"
        >
          <RefreshCwIcon className="size-3.5" />
          Try again
        </button>
      </div>
    );
  }

  const windowNote = <p className="text-muted-foreground text-sm">Covering {windowLabel}.</p>;

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {windowNote}
        <div className="border-border flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
          <InfoIcon className="text-muted-foreground size-6" />
          <p className="text-sm font-medium">No company inbox activity in this date range</p>
          <p className="text-muted-foreground max-w-md text-sm">
            Check that the selected client has a company inbox in use.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {windowNote}
      {rows.map((row) => (
        <CompanyPanel key={row.companyId} row={row} />
      ))}
    </div>
  );
}
